import { doc, getDoc, setDoc, collection, query, where, getDocs, getFirestore, runTransaction } from 'firebase/firestore';
import firebaseApp from '../../firebase/credenciales';
import { registrarAsientoCore, prepareAsientosCore, commitAsientosCore } from '../contabilidad/accountingCore';

const db = getFirestore(firebaseApp);

const estructuraCuentas = {
  '62': {
    descripcion: 'GASTOS DE PERSONAL Y DIRECTORES',
    subcuentas: {
      '621': {
        descripcion: 'REMUNERACIONES',
        subcuentas: {
          '6211': 'SUELDOS Y SALARIOS',
          '6214': 'GRATIFICACIONES',
          '6215': 'VACACIONES'
        }
      },
      '622': {
        descripcion: 'BONIFICACIONES EXTRAORDINARIAS'
      },
      '627': {
        descripcion: 'SEG., PREVISIÓN SOCIAL Y OTRAS CONT.',
        subcuentas: {
          '6271': 'RÉGIMEN DE PRESTACIONES DE SALUD (SIS)',
          '6273': 'SEGUROS (VIDA LEY)'
        }
      },
      '629': {
        descripcion: 'OTRAS REMUNERACIONES',
        subcuentas: {
          '6291': 'COMPENSACIÓN POR TIEMPO DE SERVICIOS'
        }
      }
    }
  },
  '40': {
    descripcion: 'TRIB., CONTRAP. Y APORTES AL SIST. DE PENSIONES Y DE SALUD POR PAGAR',
    subcuentas: {
      '401': {
        descripcion: 'GOBIERNO NACIONAL',
        subcuentas: {
          '4017': {
            descripcion: 'IMPUESTO A LA RENTA',
            subcuentas: {
              '40173': 'RENTA DE QUINTA CATEGORÍA'
            }
          }
        }
      },
      '403': {
        descripcion: 'INSTITUCIONES PÚBLICAS',
        subcuentas: {
          '4031': 'ESSALUD',
          '4032': 'ONP',
          '4039': 'OTRAS INSTITUCIONES'  // Puedes usar este para EsSalud Vida si no tienes uno específico
        }
      }
    }
  },
  '18': {
    descripcion: 'SERVICIOS Y OTROS CONTRATADOS POR ANTICIPADO',
    subcuentas: {
      '182': 'SEGUROS'
    }
  },
  '41': {
    descripcion: 'REMUNERACIONES Y PARTICIPACIONES POR PAGAR',
    subcuentas: {
      '411': {
        descripcion: 'REMUNERACIONES POR PAGAR',
        subcuentas: {
          '4111': 'SUELDOS Y SALARIOS POR PAGAR',
          '4114': 'GRATIFICACIONES POR PAGAR',
          '4115': 'VACACIONES POR PAGAR',
          '4119': 'BONIF. EXTRAORDINARIA POR PAGAR'
        }
      },
      '415': {
        descripcion: 'BENEFICIOS SOCIALES DE LOS TRABAJADORES POR PAGAR',
        subcuentas: {
          '4151': 'CTS POR PAGAR'
        }
      },
      '417': {
        descripcion: 'ADMINISTRADORAS DE FONDOS DE PENSIONES',
        subcuentas: {
          '4171': 'AFP PRIMA',
          '4172': 'AFP INTEGRA',
          '4173': 'AFP PROFUTURO',
          '4174': 'AFP HABITAT'
        }
      }
    }
  },
  '10': {
    descripcion: 'EFECTIVO Y EQUIVALENTES DE EFECTIVO',
    subcuentas: {
      '104': {
        descripcion: 'CUENTAS CORRIENTES',
        subcuentas: {
          '1041': 'CUENTAS CORRIENTES OPERATIVAS'
        }
      }
    }
  }
};

const CUENTAS_BASE_CONTABLE = {
  sueldos: { codigo: "621101", descripcion: "SUELDOS Y SALARIOS" },
  essalud: { codigo: "627101", descripcion: "RÉGIMEN DE PRESTACIONES DE SALUD" },
  seguro_vida: { codigo: "627102", descripcion: "SEGURO DE VIDA LEY" },
  remuneraciones_pagar: { codigo: "411101", descripcion: "SUELDOS Y SALARIOS POR PAGAR" },
  quinta_categoria: { codigo: "401701", descripcion: "RENTA DE QUINTA CATEGORÍA", contrapartida_tipo: "PASIVO" },
  onp_pagar: { codigo: "403201", descripcion: "ONP POR PAGAR", contrapartida_tipo: "PASIVO" },
  afp_pagar: { codigo: "403202", descripcion: "AFP POR PAGAR", contrapartida_tipo: "PASIVO" },
  essalud_pagar: { codigo: "403101", descripcion: "ESSALUD POR PAGAR", contrapartida_tipo: "PASIVO" },
  vida_ley_pagar: { codigo: "1821", descripcion: "SEGUROS PAGADOS POR ANTICIPADO", contrapartida_tipo: "ACTIVO" },
  adelantos_descuento: { codigo: "141201", descripcion: "ADELANTO DE REMUNERACIONES" },
  prestamos_descuento: { codigo: "141101", descripcion: "PRÉSTAMOS AL PERSONAL" },
  bancos: { codigo: "104101", descripcion: "CUENTAS CORRIENTES OPERATIVAS" },
  gratificaciones: { codigo: "621501", descripcion: "GRATIFICACIONES" },
  bonificaciones: { codigo: "621104", descripcion: "BONIFICACIONES" },
  vacaciones: { codigo: "621401", descripcion: "VACACIONES" },
  cts: { codigo: "621201", descripcion: "COMPENSACIÓN POR TIEMPO DE SERVICIOS" },
  gratificaciones_pagar: { codigo: "411201", descripcion: "GRATIFICACIONES POR PAGAR" },
  bonificaciones_pagar: { codigo: "411501", descripcion: "BONIFICACIONES POR PAGAR" },
  vacaciones_pagar: { codigo: "411301", descripcion: "VACACIONES POR PAGAR" },
  cts_pagar: { codigo: "411401", descripcion: "CTS POR PAGAR" },
};

// Función auxiliar para obtener el código de cuenta desde una configuración
const obtenerCodigoCuenta = (cuentaConfig, defaultValue) => {
  // Si es una cadena, usarla directamente
  if (typeof cuentaConfig === 'string') return cuentaConfig;

  // Si es un objeto, intentar obtener el código
  if (cuentaConfig && typeof cuentaConfig === 'object' && cuentaConfig.codigo) {
    return cuentaConfig.codigo;
  }

  // Si nada funciona, usar el valor por defecto
  return defaultValue;
};

const obtenerCuentaConfig = (cuentasConfig, key, fallbackCodigo, fallbackDescripcion) => {
  const cuentaConfig = cuentasConfig?.[key];
  const cuentaBase = CUENTAS_BASE_CONTABLE[key] || {};

  return {
    codigo: obtenerCodigoCuenta(cuentaConfig, cuentaBase.codigo || fallbackCodigo),
    descripcion: (cuentaConfig?.descripcion || cuentaBase.descripcion || fallbackDescripcion || "").trim(),
    contrapartida_tipo: cuentaConfig?.contrapartida_tipo || cuentaBase?.contrapartida_tipo || "PASIVO",
  };
};

const crearLineaAsiento = (cuenta, descripcion, monto) => {
  try {
    // Verificar que cuenta sea un valor válido
    if (!cuenta) {
      console.error(`Error: Se recibió un valor de cuenta inválido: ${cuenta}`);
      return {
        cuenta: "000000",
        descripcion_cuenta: "CUENTA NO DEFINIDA",
        monto
      };
    }

    // Asegurarse que cuenta sea string y eliminar espacios
    const cuentaStr = String(cuenta).trim();

    console.log(`Procesando cuenta: "${cuentaStr}" para ${descripcion}`);

    // Validar formato de cuenta (ahora permitimos Clase 1, 4, 6 y 18/19 para amortización)
    if (!/^\d{2,}$/.test(cuentaStr)) {
      console.error(`Error: Formato de cuenta inválido: "${cuentaStr}"`);
      return {
        cuenta: "000000",
        descripcion_cuenta: "CUENTA CON FORMATO INVÁLIDO",
        monto
      };
    }

    // Dividir la cuenta en niveles
    const nivel1 = cuentaStr.substring(0, 2);
    const nivel2 = cuentaStr.substring(0, 3);
    
    // Si ya se proporcionó una descripción específica y no es vacía, la preservamos
    // Solo buscaremos en la estructura si la descripción es genérica o falta
    let descripcionCuenta = descripcion; 
    const esDescripcionGenerica = !descripcion || 
                                descripcion === "REMUNERACIONES" || 
                                descripcion === "OTRAS REMUNERACIONES";

    // Buscar en la estructura de cuentas SOLO si es necesario
    if (estructuraCuentas[nivel1]) {
      if (cuentaStr.length === 2) {
        // Si es cuenta principal (2 dígitos)
        descripcionCuenta = estructuraCuentas[nivel1].descripcion;
      }
      else if (estructuraCuentas[nivel1].subcuentas[nivel2]) {
        if (cuentaStr.length === 3) {
          // Si es subcuenta (3 dígitos)
          descripcionCuenta = estructuraCuentas[nivel1].subcuentas[nivel2].descripcion;
        }
        else {
          // Si es subcuenta de nivel inferior
          const subcuentaClave = Object.keys(estructuraCuentas[nivel1].subcuentas[nivel2].subcuentas || {})
            .find(key => cuentaStr.startsWith(key));

          if (subcuentaClave && estructuraCuentas[nivel1].subcuentas[nivel2].subcuentas[subcuentaClave]) {
            const descValue = estructuraCuentas[nivel1].subcuentas[nivel2].subcuentas[subcuentaClave];

            // Si la descripción original era genérica, usamos la de la estructura
            if (esDescripcionGenerica) {
              if (typeof descValue === 'object') {
                descripcionCuenta = descValue.descripcion || descripcion;
              } else {
                descripcionCuenta = descValue;
              }
            }
          }
        }
      }
    }

    // Asegurar que la descripción sea una cadena de texto
    if (typeof descripcionCuenta === 'object') {
      console.error("Advertencia: La descripción de cuenta es un objeto:", descripcionCuenta);
      descripcionCuenta = descripcion || "DESCRIPCIÓN NO DISPONIBLE";
    }

    return {
      cuenta: cuentaStr,
      descripcion_cuenta: String(descripcionCuenta || "DESCRIPCIÓN NO DISPONIBLE"),
      monto: Number(monto) || 0
    };
  } catch (error) {
    console.error("Error al crear línea de asiento:", error);
    console.error("Datos recibidos:", { cuenta, descripcion, monto });

    return {
      cuenta: "000000",
      descripcion_cuenta: "ERROR: " + error.message,
      monto
    };
  }
};

export const generarAsientosContables = async (planillaId) => {
  try {
    // 1. Obtener datos de la planilla
    const planillaRef = doc(db, "pl_planillas", planillaId);
    const planillaDoc = await getDoc(planillaRef);
    if (!planillaDoc.exists()) throw new Error("Planilla no encontrada");
    const planilla = planillaDoc.data();

    // 2. Obtener configuración contable
    const configRef = doc(db, "pl_config_contable", planilla.ruc_empresa);
    const configDoc = await getDoc(configRef);
    const config = configDoc.exists()
      ? configDoc.data()
      : { cuentas: { ...CUENTAS_BASE_CONTABLE } };
    const cuentasConfig = {
      ...CUENTAS_BASE_CONTABLE,
      ...(config.cuentas || {})
    };

    // 2.2 Obtener configuración general (para saber si es SIS)
    const generalConfigRef = doc(db, "pl_config", planilla.ruc_empresa);
    const generalConfigSnap = await getDoc(generalConfigRef);
    const generalConfig = generalConfigSnap.exists() ? generalConfigSnap.data() : {};
    const esRegimenSIS = generalConfig.tipo_seguro_micro === 'SIS';

    // 3. Obtener detalles de la planilla
    const detalleQuery = query(
      collection(db, "pl_planilla_detalle"),
      where("planilla_id", "==", planillaId)
    );
    const detalleSnapshot = await getDocs(detalleQuery);
    const detalles = [];
    detalleSnapshot.forEach(doc => detalles.push(doc.data()));

    // Calcular totales específicos
    const totalAFP = getTotalAFP(detalles);
    const totalONP = getTotalONP(detalles);
    const totalQuinta = getTotalQuintaCategoria(detalles);
    const totalAdelantos = getTotalAdelantos(detalles);
    const totalPrestamos = getTotalPrestamos(detalles);
    
    // Nueva lógica para separar SIS/EsSalud y Vida Ley sumando desde detalles
    const totalVidaLey = detalles.reduce((sum, d) => sum + (Number(d.aportaciones?.vida_ley || 0)), 0);
    const totalEssalud = detalles.reduce((sum, d) => sum + (Number(d.aportaciones?.essalud || 0)), 0);
    
    // H011: Totales adicionales para equilibrar el asiento
    const totalEssaludVida = getTotalEssaludVida(detalles);
    const totalDescuentoFaltas = getTotalDescuentoFaltas(detalles);
    const totalDescuentoDSO = getTotalDescuentoDSO(detalles);
    const totalDescuentoTardanzas = getTotalDescuentoTardanzas(detalles);
    const totalIngresosBruto = getTotalIngresosBruto(detalles);
    
    const totalNeto = Number(planilla.total_neto || 0);

    // Construir las líneas del haber para el asiento de provisión
    const lineasHaber = [];

    // Añadir solo líneas con montos mayores que cero
    const cuentaRemuneracionesPagar = obtenerCuentaConfig(
      cuentasConfig,
      "remuneraciones_pagar",
      "411101",
      "SUELDOS Y SALARIOS POR PAGAR"
    );
    lineasHaber.push(crearLineaAsiento(
      cuentaRemuneracionesPagar.codigo,
      cuentaRemuneracionesPagar.descripcion,
      totalNeto
    ));

    if (totalQuinta > 0) {
      const cuentaQuinta = obtenerCuentaConfig(
        cuentasConfig,
        "quinta_categoria",
        "401701",
        "RENTA DE QUINTA CATEGORÍA"
      );
      lineasHaber.push(crearLineaAsiento(
        cuentaQuinta.codigo,
        cuentaQuinta.descripcion,
        totalQuinta
      ));
    }

    if (totalONP > 0) {
      const cuentaOnp = obtenerCuentaConfig(
        cuentasConfig,
        "onp_pagar",
        "403201",
        "ONP POR PAGAR"
      );
      lineasHaber.push(crearLineaAsiento(
        cuentaOnp.codigo,
        cuentaOnp.descripcion,
        totalONP
      ));
    }

    if (totalAFP > 0) {
      const cuentaAfp = obtenerCuentaConfig(
        cuentasConfig,
        "afp_pagar",
        "403202",
        "AFP POR PAGAR"
      );
      lineasHaber.push(crearLineaAsiento(
        cuentaAfp.codigo,
        cuentaAfp.descripcion,
        totalAFP
      ));
    }

    const totalEssaludYPagar = Number(((totalEssalud || 0) + (totalEssaludVida || 0)).toFixed(2));
    if (totalEssaludYPagar > 0) {
      const cuentaEssaludPagar = obtenerCuentaConfig(
        cuentasConfig,
        "essalud_pagar",
        "403101",
        "ESSALUD POR PAGAR"
      );
      const esAmortizacionEssalud = cuentaEssaludPagar.contrapartida_tipo === "ACTIVO" || String(cuentaEssaludPagar.codigo || "").startsWith("1");
      
      lineasHaber.push(crearLineaAsiento(
        cuentaEssaludPagar.codigo,
        esAmortizacionEssalud
          ? `AMORTIZACIÓN ${esRegimenSIS ? "SIS" : "ESSALUD"}`
          : cuentaEssaludPagar.descripcion,
        totalEssaludYPagar
      ));
    }

    if (totalVidaLey > 0) {
      const cuentaVidaLeyPagar = obtenerCuentaConfig(
        cuentasConfig,
        "vida_ley_pagar",
        "1821",
        "SEGURO VIDA LEY POR PAGAR"
      );
      const esAmortizacionVidaLey = cuentaVidaLeyPagar.contrapartida_tipo === "ACTIVO" || String(cuentaVidaLeyPagar.codigo || "").startsWith("1");

      lineasHaber.push(crearLineaAsiento(
        cuentaVidaLeyPagar.codigo,
        esAmortizacionVidaLey ? "AMORTIZACIÓN SEGURO DE VIDA LEY" : cuentaVidaLeyPagar.descripcion,
        totalVidaLey
      ));
    }

    // H011: Descuento por faltas (reducción de la obligación con trabajadores)
    if (totalDescuentoFaltas > 0) {
      const cuentaSueldosHaber = obtenerCuentaConfig(
        cuentasConfig,
        "sueldos",
        "621101",
        "SUELDOS Y SALARIOS"
      );
      lineasHaber.push(crearLineaAsiento(
        cuentaSueldosHaber.codigo,
        "DESCUENTO POR FALTAS",
        totalDescuentoFaltas
      ));
    }

    // H013-E2: DSO — Descuento proporcional del descanso semanal obligatorio
    if (totalDescuentoDSO > 0) {
      const cuentaSueldosHaber = obtenerCuentaConfig(
        cuentasConfig,
        "sueldos",
        "621101",
        "SUELDOS Y SALARIOS"
      );
      lineasHaber.push(crearLineaAsiento(
        cuentaSueldosHaber.codigo,
        "DSO - DESC. PROP. DESCANSO SEMANAL",
        totalDescuentoDSO
      ));
    }

    // H011: Descuento por tardanzas (reducción de la obligación con trabajadores)
    if (totalDescuentoTardanzas > 0) {
      const cuentaSueldosHaber = obtenerCuentaConfig(
        cuentasConfig,
        "sueldos",
        "621101",
        "SUELDOS Y SALARIOS"
      );
      lineasHaber.push(crearLineaAsiento(
        cuentaSueldosHaber.codigo,
        "DESCUENTO POR TARDANZAS",
        totalDescuentoTardanzas
      ));
    }

    if (totalAdelantos > 0) {
      const cuentaAdelantos = obtenerCuentaConfig(
        cuentasConfig,
        "adelantos_descuento",
        "141201",
        "ADELANTO DE REMUNERACIONES"
      );
      lineasHaber.push(crearLineaAsiento(cuentaAdelantos.codigo, cuentaAdelantos.descripcion, totalAdelantos));
    }

    if (totalPrestamos > 0) {
      const cuentaPrestamos = obtenerCuentaConfig(
        cuentasConfig,
        "prestamos_descuento",
        "141101",
        "PRÉSTAMOS AL PERSONAL"
      );
      lineasHaber.push(crearLineaAsiento(cuentaPrestamos.codigo, cuentaPrestamos.descripcion, totalPrestamos));
    }

    // 4. Crear asiento de provisión
    const asientoProvision = {
      ruc_empresa: String(planilla.ruc_empresa || ""),
      planilla_id: String(planillaId || ""),
      periodo: String(planilla.periodo || ""),
      tipo_asiento: "PROVISION",
      fecha: new Date(),
      glosa: `PROVISIÓN DE PLANILLA ${planilla.periodo || ""}`,
      debe: [
        (() => {
          const cuentaSueldos = obtenerCuentaConfig(
            cuentasConfig,
            "sueldos",
            "621101",
            "SUELDOS Y SALARIOS"
          );
          return crearLineaAsiento(cuentaSueldos.codigo, cuentaSueldos.descripcion, totalIngresosBruto);
        })(),
        (() => {
          const cuentaEssalud = obtenerCuentaConfig(
            cuentasConfig,
            "essalud",
            "627101",
            esRegimenSIS ? "RÉGIMEN DE PRESTACIONES DE SALUD (SIS)" : "ESSALUD"
          );
          return crearLineaAsiento(cuentaEssalud.codigo, cuentaEssalud.descripcion, Number(((totalEssalud || 0) + (totalEssaludVida || 0)).toFixed(2)));
        })(),
        ...(totalVidaLey > 0 ? [(() => {
          const cuentaVidaLeyGasto = obtenerCuentaConfig(
            cuentasConfig,
            "seguro_vida",
            "627102",
            "SEGURO DE VIDA LEY"
          );
          return crearLineaAsiento(cuentaVidaLeyGasto.codigo, cuentaVidaLeyGasto.descripcion, totalVidaLey);
        })()] : [])
      ],
      haber: lineasHaber,
      total_debe: Number(((totalIngresosBruto || 0) + (totalEssalud || 0) + (totalEssaludVida || 0) + (totalVidaLey || 0)).toFixed(2)) || 0,
      total_haber: Number(((totalNeto || 0) + (totalQuinta || 0) + (totalONP || 0) + (totalAFP || 0) + (totalEssaludYPagar || 0) + (totalVidaLey || 0) + (totalDescuentoFaltas || 0) + (totalDescuentoDSO || 0) + (totalDescuentoTardanzas || 0) + (totalAdelantos || 0) + (totalPrestamos || 0)).toFixed(2)) || 0,
      estado: "REGISTRADO",
      created_at: new Date(),
      updated_at: new Date()
    };

    // H011: Validación defensiva - asiento de provisión debe cuadrar
    const diferenciaProvision = Math.round(((asientoProvision.total_debe || 0) - (asientoProvision.total_haber || 0)) * 100) / 100;
    if (Math.abs(diferenciaProvision) > 0.01) {
      throw new Error(
        `Asiento de provisión descuadrado: Debe=${asientoProvision.total_debe}, Haber=${asientoProvision.total_haber}, Diferencia=${diferenciaProvision}`
      );
    }

    // 5. Crear asiento de pago
    const asientoPago = {
      ruc_empresa: planilla.ruc_empresa,
      planilla_id: planillaId,
      periodo: planilla.periodo,
      tipo_asiento: "PAGO",
      fecha: new Date(),
      glosa: `PAGO DE PLANILLA ${planilla.periodo}`,
      debe: [
        (() => {
          const cuentaRemPagar = obtenerCuentaConfig(
            cuentasConfig,
            "remuneraciones_pagar",
            "411101",
            "SUELDOS Y SALARIOS POR PAGAR"
          );
          return crearLineaAsiento(cuentaRemPagar.codigo, cuentaRemPagar.descripcion, totalNeto);
        })()
      ],
      haber: [
        (() => {
          const cuentaBancos = obtenerCuentaConfig(
            cuentasConfig,
            "bancos",
            "104101",
            "CUENTAS CORRIENTES OPERATIVAS"
          );
          return crearLineaAsiento(cuentaBancos.codigo, cuentaBancos.descripcion, totalNeto);
        })()
      ],
      total_debe: Number((totalNeto || 0).toFixed(2)),
      total_haber: Number((totalNeto || 0).toFixed(2)),
      estado: "PENDIENTE",
      created_at: new Date(),
      updated_at: new Date()
    };

    // 7. Crear asiento de provisiones de beneficios sociales
    const totalProvGrati = detalles.reduce((sum, d) => sum + (d.provisiones?.gratificaciones || 0), 0);
    const totalProvBono = detalles.reduce((sum, d) => sum + (d.provisiones?.bono_extraordinario || 0), 0);
    const totalProvVac = detalles.reduce((sum, d) => sum + (d.provisiones?.vacaciones || 0), 0);
    const totalProvEssaludVac = detalles.reduce((sum, d) => sum + (d.provisiones?.essalud_vacaciones || 0), 0);
    const totalProvCTS = detalles.reduce((sum, d) => sum + (d.provisiones?.cts || 0), 0);

    const lineasDebeProv = [];
    const lineasHaberProv = [];

    if (totalProvGrati > 0) {
      const cuentaGratificaciones = obtenerCuentaConfig(
        cuentasConfig,
        "gratificaciones",
        "621501",
        "GRATIFICACIONES"
      );
      const cuentaGratificacionesPagar = obtenerCuentaConfig(
        cuentasConfig,
        "gratificaciones_pagar",
        "411201",
        "GRATIFICACIONES POR PAGAR"
      );
      lineasDebeProv.push(crearLineaAsiento(cuentaGratificaciones.codigo, cuentaGratificaciones.descripcion, totalProvGrati));
      lineasHaberProv.push(crearLineaAsiento(cuentaGratificacionesPagar.codigo, cuentaGratificacionesPagar.descripcion, totalProvGrati));
    }
    if (totalProvBono > 0) {
      const cuentaBonificaciones = obtenerCuentaConfig(
        cuentasConfig,
        "bonificaciones",
        "621104",
        "BONIFICACIONES"
      );
      const cuentaBonificacionesPagar = obtenerCuentaConfig(
        cuentasConfig,
        "bonificaciones_pagar",
        "411501",
        "BONIFICACIONES POR PAGAR"
      );
      lineasDebeProv.push(crearLineaAsiento(cuentaBonificaciones.codigo, cuentaBonificaciones.descripcion, totalProvBono));
      lineasHaberProv.push(crearLineaAsiento(cuentaBonificacionesPagar.codigo, cuentaBonificacionesPagar.descripcion, totalProvBono));
    }
    if (totalProvVac > 0) {
      const cuentaVacaciones = obtenerCuentaConfig(
        cuentasConfig,
        "vacaciones",
        "621401",
        "VACACIONES"
      );
      const cuentaVacacionesPagar = obtenerCuentaConfig(
        cuentasConfig,
        "vacaciones_pagar",
        "411301",
        "VACACIONES POR PAGAR"
      );
      lineasDebeProv.push(crearLineaAsiento(cuentaVacaciones.codigo, cuentaVacaciones.descripcion, totalProvVac));
      lineasHaberProv.push(crearLineaAsiento(cuentaVacacionesPagar.codigo, cuentaVacacionesPagar.descripcion, totalProvVac));
    }
    
    // IMPORTANTE: En el régimen SIS (Microempresa), las provisiones de beneficios NO generan salud adicional (S/ 15 tarifa plana)
    if (totalProvEssaludVac > 0 && !esRegimenSIS) {
      const cuentaEssaludGasto = obtenerCuentaConfig(
        cuentasConfig,
        "essalud",
        "627101",
        "RÉGIMEN DE PRESTACIONES DE SALUD (VAC)"
      );
      const cuentaEssaludPagar = obtenerCuentaConfig(
        cuentasConfig,
        "essalud_pagar",
        "403101",
        "ESSALUD POR PAGAR"
      );
      lineasDebeProv.push(crearLineaAsiento(cuentaEssaludGasto.codigo, cuentaEssaludGasto.descripcion, totalProvEssaludVac));
      lineasHaberProv.push(crearLineaAsiento(cuentaEssaludPagar.codigo, cuentaEssaludPagar.descripcion, totalProvEssaludVac));
    }
    
    if (totalProvCTS > 0) {
      const cuentaCts = obtenerCuentaConfig(
        cuentasConfig,
        "cts",
        "621201",
        "COMPENSACIÓN POR TIEMPO DE SERVICIOS"
      );
      const cuentaCtsPagar = obtenerCuentaConfig(
        cuentasConfig,
        "cts_pagar",
        "411401",
        "CTS POR PAGAR"
      );
      lineasDebeProv.push(crearLineaAsiento(cuentaCts.codigo, cuentaCts.descripcion, totalProvCTS));
      lineasHaberProv.push(crearLineaAsiento(cuentaCtsPagar.codigo, cuentaCtsPagar.descripcion, totalProvCTS));
    }

    // Calcular el total real del asiento (excluyendo salud si es SIS)
    const actualTotalEssaludVac = esRegimenSIS ? 0 : totalProvEssaludVac;
    const totalAsientoProv = totalProvGrati + totalProvBono + totalProvVac + actualTotalEssaludVac + totalProvCTS;

    const asientoBeneficios = {
      ruc_empresa: planilla.ruc_empresa,
      planilla_id: planillaId,
      periodo: planilla.periodo,
      tipo_asiento: "PROVISIONES",
      fecha: new Date(),
      glosa: `PROVISIÓN DE BENEFICIOS SOCIALES ${planilla.periodo}`,
      debe: lineasDebeProv,
      haber: lineasHaberProv,
      total_debe: Number(totalAsientoProv.toFixed(2)),
      total_haber: Number(totalAsientoProv.toFixed(2)),
      estado: "REGISTRADO",
      created_at: new Date(),
      updated_at: new Date()
    };

    // Determinar la fecha del asiento como el último día del periodo de la planilla
    let fechaAsientoStr = "";
    try {
      const [y, m] = planilla.periodo.split("-").map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      fechaAsientoStr = `${planilla.periodo}-${String(lastDay).padStart(2, "0")}`;
    } catch (e) {
      fechaAsientoStr = new Date().toISOString().slice(0, 10);
    }

    asientoProvision.fecha = fechaAsientoStr;
    asientoPago.fecha = fechaAsientoStr;
    asientoBeneficios.fecha = fechaAsientoStr;

    asientoProvision.moneda = asientoProvision.moneda || "PEN";
    asientoPago.moneda = asientoPago.moneda || "PEN";
    asientoBeneficios.moneda = asientoBeneficios.moneda || "PEN";

    // Phase 1: ALL reads happen here (period lock, idempotency, CUO)
    const { prepared, cuoRef, cuoValue } = await prepareAsientosCore({
      rucEmpresa: planilla.ruc_empresa,
      asientos: [
        {
          asientoId: `${planillaId}_PROVISION`,
          payload: { ...asientoProvision, moneda: "PEN", tipoCambio: 1 },
          idempotencyKey: `${planilla.ruc_empresa}_PLANILLA_${planillaId}_DEVENGAMIENTO_v1`,
          metadata: { sourceModule: "PLANILLAS", sourceType: "PROVISION", sourceId: planillaId, sourceDocumentId: planillaId },
        },
        {
          asientoId: `${planillaId}_PAGO`,
          payload: { ...asientoPago, moneda: "PEN", tipoCambio: 1 },
          idempotencyKey: `${planilla.ruc_empresa}_PLANILLA_${planillaId}_PAGO_v1`,
          metadata: { sourceModule: "PLANILLAS", sourceType: "PAGO", sourceId: planillaId, sourceDocumentId: planillaId },
        },
        {
          asientoId: `${planillaId}_PROVISIONES`,
          payload: { ...asientoBeneficios, moneda: "PEN", tipoCambio: 1 },
          idempotencyKey: `${planilla.ruc_empresa}_PLANILLA_${planillaId}_PROVISIONES_v1`,
          metadata: { sourceModule: "PLANILLAS", sourceType: "PROVISION_BENEFICIOS", sourceId: planillaId, sourceDocumentId: planillaId },
        },
      ],
    });

    // Phase 2: ALL writes happen atomically (zero reads inside)
    if (prepared.length > 0) {
      await commitAsientosCore({ prepared, cuoRef, cuoValue });
    }

    const planRef = doc(db, "pl_planillas", planillaId);
    await setDoc(planRef, {
      asiento_provision_id: `${planillaId}_PROVISION`,
      monto_pagado: 0,
      saldo_pendiente: Number((planilla.total_neto || 0).toFixed(2)),
      estado_pago: "PENDIENTE"
    }, { merge: true });

    return {
      success: true,
      message: "Asientos contables generados correctamente"
    };

  } catch (error) {
    console.error("Error al generar asientos:", error);
    throw error;
  }
};

// Funciones auxiliares para calcular totales
export const getTotalAFP = (detalles) => {
  return detalles.reduce((total, d) => {
    if (d.regimen_pensionario === 'AFP') {
      const sistemaPension = d.descuentos?.sistema_pension || {};
      const totalAFP = (sistemaPension.fondo || 0) +
        (sistemaPension.seguro || 0) +
        (sistemaPension.comision || 0);
      return total + totalAFP;
    }
    return total;
  }, 0);
};

export const getTotalONP = (detalles) => {
  return detalles.reduce((total, d) => {
    if (d.regimen_pensionario === 'ONP') {
      return total + (d.descuentos?.sistema_pension?.onp || 0);
    }
    return total;
  }, 0);
};

export const getTotalEssaludVida = (detalles) => {
  return detalles.reduce((total, d) => {
    // Es importante buscar correctamente este campo en la estructura de datos
    const essaludVida =
      (d.descuentos?.essalud_vida) ||            // Opción 1: Directamente en descuentos
      (d.descuentos?.otros?.essalud_vida) ||     // Opción 2: En subcategoría "otros" 
      (d.descuentos?.seguros?.essalud_vida) ||   // Opción 3: En subcategoría "seguros"
      0;                                          // Si no existe, usar 0

    console.log(`Trabajador: ${d.trabajador_nombre}, EsSalud Vida: ${essaludVida}`);
    return total + essaludVida;
  }, 0);
};

export const getTotalQuintaCategoria = (detalles) => {
  return detalles.reduce((total, d) => {
    return total + (d.descuentos?.renta_5ta || 0);
  }, 0);
};

export const getTotalAdelantos = (detalles) => {
  return detalles.reduce((total, d) => {
    const adelanto = 
      (d.descuentos?.adelantos) || 
      (d.descuentos?.adelanto) || 
      (d.adelantos_monto) || 0;
    return total + Number(adelanto);
  }, 0);
};

export const getTotalPrestamos = (detalles) => {
  return detalles.reduce((total, d) => {
    const prestamo = 
      (d.descuentos?.prestamos) || 
      (d.descuentos?.prestamo) || 0;
    return total + Number(prestamo);
  }, 0);
};

export const getTotalDescuentoFaltas = (detalles) => {
  return detalles.reduce((total, d) => {
    return total + (d.descuentos?.descuento_faltas || 0);
  }, 0);
};

export const getTotalDescuentoDSO = (detalles) => {
  return detalles.reduce((total, d) => {
    return total + (d.descuentos?.descuento_dso || 0);
  }, 0);
};

export const getTotalDescuentoTardanzas = (detalles) => {
  return detalles.reduce((total, d) => {
    return total + (d.descuentos?.descuento_tardanzas || 0);
  }, 0);
};

export const getTotalIngresosBruto = (detalles) => {
  return detalles.reduce((total, d) => {
    return total + (d.total_ingresos_bruto || d.total_ingresos || 0);
  }, 0);
};

export default generarAsientosContables;
